/**
 * CommunitySearch — full-community search results page.
 * Reads `?q=` from the URL; user can refine via the sub-nav search input
 * or a bigger input at the top of the page.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { formatDistanceToNow } from 'date-fns';
import ToolSelector from '../components/ToolSelector';
import ForumSubNav from '../components/community/ForumSubNav';
import CommunityAd from '../components/community/CommunityAd';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, MessageSquare, Pin, Lock, BarChart3, Calendar, Megaphone } from 'lucide-react';

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const KIND_ICON = { poll: BarChart3, event: Calendar, announcement: Megaphone };

export default function CommunitySearch() {
  const { API } = useContext(AuthContext);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = params.get('q') || '';
  const [q, setQ] = useState(initial);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const runSearch = async (query) => {
    if (!query.trim()) { setResults([]); setTotal(0); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/forum/search?q=${encodeURIComponent(query.trim())}`);
      setResults(r.data.topics || []);
      setTotal(r.data.total || 0);
    } catch { setResults([]); setTotal(0); }
    finally { setLoading(false); }
  };

  useEffect(() => { runSearch(initial); }, [initial, API]);

  const submit = (e) => {
    e.preventDefault();
    setParams({ q });
    runSearch(q);
  };

  const openTopic = (t) => {
    if (t.lesson_id) navigate(`/lesson/${t.lesson_id}`);
    else navigate(`/community/t/${t.id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <ForumSubNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4">
          <div className="eyebrow text-primary">Community search</div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground">
            {q.trim() ? `Results for "${q}"` : 'Search the community'}
          </h1>
          {q.trim() && !loading && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="search-count">
              {total} {total === 1 ? 'match' : 'matches'}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="flex items-center gap-2 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              autoFocus
              type="search"
              placeholder="Search topics, replies, tags…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              className="h-11 pl-9 text-base"
            />
          </div>
          <Button type="submit" className="rounded-full font-bold h-11 px-5" data-testid="search-submit">Search</Button>
        </form>

        {/* Rotating ad — kept above results so it always has viewport time */}
        <CommunityAd placement="topic" className="mb-4" />

        <div className="pro-card p-2" data-testid="search-results">
          {loading && <div className="p-6 text-center text-sm text-muted-foreground">Searching…</div>}
          {!loading && q.trim() && results.length === 0 && (
            <div className="p-8 text-center">
              <Search className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm font-semibold text-foreground">No matches</p>
              <p className="text-xs text-muted-foreground mt-1">Try a broader query or check the category pages.</p>
            </div>
          )}
          {!loading && results.map((t) => {
            const KindIcon = KIND_ICON[t.kind];
            const color = t.category?.color || '#0EA5E9';
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => openTopic(t)}
                data-testid={`search-result-${t.id}`}
                className="w-full text-left rounded-lg border border-transparent hover:border-border hover:bg-card px-3 py-2.5 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="mt-0.5 h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                    style={{ backgroundColor: `${color}25`, color }}
                    aria-hidden="true"
                  >
                    {(t.author?.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {t.is_pinned && <Pin className="w-3 h-3 text-amber-500" />}
                      {t.is_locked && <Lock className="w-3 h-3 text-muted-foreground" />}
                      <span className="font-heading text-sm font-bold text-foreground truncate">{t.title}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      {t.category && (
                        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold"
                              style={{ backgroundColor: `${color}20`, color }}>
                          {t.category.name}
                        </span>
                      )}
                      {KindIcon && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground bg-muted rounded px-1.5 py-0.5 uppercase tracking-widest">
                          <KindIcon className="w-2.5 h-2.5" /> {t.kind}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground truncate">
                        by {t.author?.name || 'Unknown'} · {rel(t.last_activity_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs font-bold text-muted-foreground flex-shrink-0">
                    <MessageSquare className="w-3 h-3" /> {t.reply_count || 0}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
